import createClient, { type Middleware } from 'openapi-fetch'

import type { paths } from './types.gen'

/** Problem detail as returned by every failing backend route (RFC 9457). */
export interface Problem {
  type: string
  title: string
  status: number
  detail: string
  errors?: { field: string; message: string }[]
  blockers?: { entity: string; count: number; examples?: string[] }[]
}

/** Thrown for any non-2xx response so callers can `catch` one shape. */
export class ApiError extends Error {
  readonly problem: Problem

  constructor(problem: Problem) {
    super(problem.detail || problem.title)
    this.name = 'ApiError'
    this.problem = problem
  }

  get status() {
    return this.problem.status
  }

  /** Field errors keyed by field name, for `react-hook-form` `setError`. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.problem.errors ?? []).map((e) => [e.field, e.message]))
  }
}

/** Subscribers notified when the backend reports the session is gone. */
const unauthorizedHandlers = new Set<() => void>()

/**
 * Register a callback for `401` responses. The auth gate uses this to swap the
 * app for the login screen instead of showing an error toast on every request.
 */
export function onUnauthorized(handler: () => void): () => void {
  unauthorizedHandlers.add(handler)
  return () => {
    unauthorizedHandlers.delete(handler)
  }
}

const problemMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return response

    let problem: Problem
    try {
      problem = (await response.clone().json()) as Problem
    } catch {
      problem = {
        type: '/problems/unknown',
        title: response.statusText || 'Request failed',
        status: response.status,
        detail: `The server responded with ${response.status}.`,
      }
    }

    if (response.status === 401) {
      unauthorizedHandlers.forEach((handler) => handler())
    }

    throw new ApiError(problem)
  },
}

/**
 * The SPA is served from the same origin as the API, so the origin is the base.
 * It is spelled out rather than left relative because `fetch` outside a browser
 * (jsdom under Vitest) cannot resolve a relative URL.
 */
const baseUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.origin

export const api = createClient<paths>({
  baseUrl,
  credentials: 'same-origin',
  // Resolve `fetch` per call instead of letting openapi-fetch capture
  // `globalThis.fetch` when this module is first imported. Without the
  // indirection a test double installed later (msw) is bypassed and the request
  // escapes to the real network.
  fetch: (request) => globalThis.fetch(request),
})

api.use(problemMiddleware)
